import { Box, Stack, Typography, Grid2 } from '@mui/material';
import useBundleTranslation from 'i18n';
import { useDispatch, useSelector } from 'react-redux';
import { ICustomService, IDomain, setSelectedDomains } from 'store/conciergeSlice';
import { useMemo } from 'react';
import IconHandler from 'components/common/icon/IconHandler';
import { merge } from 'lodash';
import { ICustomStyles } from 'app/concierge/panel';
import defaultStyles from '../styles';

interface IProps {
    tools: string[];
    messageCustomService: ICustomService | null;
}

const DEFAULT_DOMAINS = ['search', 'glossary', 'docs', 'faq'];

export default function ToolSelector({ tools, messageCustomService }: IProps) {
    const { t } = useBundleTranslation(['app/concierge/widget']);
    const dispatch = useDispatch();
    const availableDomains = useSelector((state: any) => state.concierge.availableDomains) as IDomain[];

    const selectDomain = (name: string) => {
        dispatch(setSelectedDomains([name]));
    };

    const domains: IDomain[] = useMemo(() => {
        const result: IDomain[] = [];

        availableDomains.forEach((domain) => {
            if (domain.customServices && domain.customServices.length > 0) {
                domain.customServices.forEach((service) => {
                    if (tools.includes(service.name)) {
                        if (result.find((d) => d.name === domain.name)) {
                            return;
                        }
                        if (messageCustomService && !DEFAULT_DOMAINS.includes(domain.name)) {
                            if (
                                service.domain_id === messageCustomService.domain_id &&
                                service.name === messageCustomService.name
                            ) {
                                result.push(domain);
                            } else {
                                return;
                            }
                        } else {
                            result.push(domain);
                        }
                    }
                });
            }
        });

        return result;
    }, [tools, messageCustomService, availableDomains]);

    if (domains.length > 5) {
        return null;
    }

    return (
        <Stack spacing={1}>
            <Typography>{t('message.found_answers')}</Typography>
            <Grid2 container direction="row" alignItems="center" justifyContent={'flex-start'} spacing={0.5}>
                <Typography fontWeight={600}>{t('message.continue_with')}</Typography>
                {domains.map((domain, idx: number) => (
                    <DomainBadge key={idx} domain={domain} selectDomain={selectDomain} />
                ))}
            </Grid2>
        </Stack>
    );
}

function DomainBadge({ domain, selectDomain }: { domain: IDomain; selectDomain: (name: string) => void }) {
    const selectedDomains = useSelector((state: any) => state.concierge.selectedDomains) as string[];
    const isSelected = selectedDomains.length === 1 && selectedDomains.includes(domain.name);
    const customStyles = useSelector((state: any) => state.concierge.customStyles) as ICustomStyles;

    const styles = useMemo(() => {
        return merge({}, defaultStyles, customStyles?.activeChatMessage) as typeof defaultStyles;
    }, [customStyles]);

    const iconProps = {
        type: domain.icon_ind,
        value: domain.icon_ind === 'upload' ? `data:image/png;base64,${domain.icon_data}` : domain.icon_name,
    };

    return (
        <Box sx={styles.tool} className={isSelected ? 'selected' : ''} onClick={() => selectDomain(domain.name)}>
            <IconHandler icon={iconProps} sx={{ fontSize: '14px' }} />
            {domain.title}
        </Box>
    );
}
